import {useEffect, useState, useCallback} from 'react';
import * as api from '../services/dailyBettingBoard';
import {LeaderboardItemType} from '@/common-pages/leaderboard/types';
import {GetYesterdayRewardAndPopupResponse} from '../services/dailyBettingBoard';

export function useDailyBettingBoard(dateOffset: number = 0) {
  const [data, setData] = useState<LeaderboardItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 新增奖池金额状态
  const [jackpot, setJackpot] = useState<number>(0);
  const [jackpotLoading, setJackpotLoading] = useState(true);

  // 新增当前用户排名状态
  const [currentUser, setCurrentUser] = useState<LeaderboardItemType | null>(
    null,
  );
  const [currentUserLoading, setCurrentUserLoading] = useState(true);

  // 新增今日详情状态
  const [todayDetail, setTodayDetail] =
    useState<api.GetTodayBettingDetailResponse | null>(null);
  const [todayDetailLoading, setTodayDetailLoading] = useState(true);

  // 新增昨日奖励和回归信息状态 - 统一数据源
  const [yesterdayRewardInfo, setYesterdayRewardInfo] =
    useState<GetYesterdayRewardAndPopupResponse | null>(null);
  const [yesterdayRewardLoading, setYesterdayRewardLoading] = useState(true);

  // 根据偏移量获取日期
  const getDateWithOffset = useCallback(() => {
    return Date.now() - dateOffset * 24 * 60 * 60 * 1000;
  }, [dateOffset]);

  // 获取奖池金额
  const loadJackpot = useCallback(
    async (skipLoading = false) => {
      if (!skipLoading) {
        setJackpotLoading(true);
      }
      try {
        const result = await api.getDailyBettingJackpot({
          date: getDateWithOffset().toString(),
        });
        setJackpot(Math.floor(result));
      } catch (err) {
        console.error(err);
      } finally {
        if (!skipLoading) {
          setJackpotLoading(false);
        }
      }
    },
    [getDateWithOffset],
  );

  // 获取当前用户排名
  const loadCurrentUser = useCallback(
    async (skipLoading = false) => {
      if (!skipLoading) {
        setCurrentUserLoading(true);
      }
      try {
        const result = await api.getDailyBettingUserRank({
          date: getDateWithOffset().toString(),
        });
        if (result && result.userId) {
          const transformedUser: LeaderboardItemType = {
            id: result.userId,
            username: result.username,
            userPhone: result.userPhone,
            rank: result.rank,
            bonus: result.rewardValue,
            point: result.totalScore,
            avatar: result.userAvatar, // 用户排名接口没有头像字段
            rewardRate: result.rewardRate || 0,
            rewardRateStr: result.rewardRateStr || '0%',
          };
          setCurrentUser(transformedUser);
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        setCurrentUser(null);
      } finally {
        if (!skipLoading) {
          setCurrentUserLoading(false);
        }
      }
    },
    [getDateWithOffset],
  );

  // 获取今日详情
  const loadTodayDetail = useCallback(async (skipLoading = false) => {
    if (!skipLoading) {
      setTodayDetailLoading(true);
    }
    try {
      const result = await api.getTodayBettingDetail();
      setTodayDetail(result);
    } catch (err) {
      setTodayDetail(null);
    } finally {
      if (!skipLoading) {
        setTodayDetailLoading(false);
      }
    }
  }, []);

  // 获取昨日奖励和回归信息 - 统一接口调用
  const loadYesterdayRewardInfo = useCallback(async (skipLoading = false) => {
    if (!skipLoading) {
      setYesterdayRewardLoading(true);
    }
    try {
      const result = await api.getYesterdayRewardAndPopup();
      setYesterdayRewardInfo(result);
    } catch (err) {
      console.error(err);
      setYesterdayRewardInfo(null);
    } finally {
      if (!skipLoading) {
        setYesterdayRewardLoading(false);
      }
    }
  }, []);

  // RechargeBoard 专门的数据加载逻辑
  const loadRechargeBoardData = useCallback(async () => {
    const res = await api.getDailyBettingRanking({
      group: 0,
      date: getDateWithOffset().toString(),
    });

    if (res && Array.isArray(res)) {
      const transformedData: LeaderboardItemType[] = res.map(item => ({
        username: item.username,
        bonus: item.rewardValue,
        rank: item.rank,
        avatar: item.userAvatar,
        id: item.userId,
        point: item.totalScore,
        userPhone: item.userPhone,
        rewardRate: item.rewardRate || 0,
        rewardRateStr: item.rewardRateStr,
      }));

      // 如果列表长度不足20个，补齐默认值
      const paddedData = [...transformedData];
      while (paddedData.length < 20) {
        paddedData.push({
          id: `default-${paddedData.length + 1}`,
          username: '',
          avatar: '',
          rank: paddedData.length + 1,
          bonus: 0,
          point: 0,
          userPhone: '',
          rewardRate: 0,
        });
      }
      return paddedData;
    }

    return [];
  }, [getDateWithOffset]);

  // 加载数据的函数
  const loadData = useCallback(
    async (skipLoading = false) => {
      if (!skipLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await loadRechargeBoardData();
        setData(result);
      } catch (err) {
        setError('获取数据失败');
      } finally {
        if (!skipLoading) {
          setLoading(false);
        }
      }
    },
    [loadRechargeBoardData],
  );

  // 初始加载和日期变化时重新加载
  useEffect(() => {
    setData([]);
    loadData();
    loadJackpot();
    loadCurrentUser();
  }, [dateOffset, loadData, loadJackpot, loadCurrentUser]);

  // loadTodayDetail 和 loadYesterdayRewardInfo 只在组件初始化时执行一次
  useEffect(() => {
    loadTodayDetail();
    loadYesterdayRewardInfo();
  }, [loadTodayDetail, loadYesterdayRewardInfo]);

  // 刷新数据
  const refresh = useCallback(
    (skipLoading = false) => {
      loadData(skipLoading);
      loadJackpot(skipLoading);
      loadCurrentUser(skipLoading);
      // 注意：loadTodayDetail 和 loadYesterdayRewardInfo 不包含在刷新中，因为它们只在初始化时执行一次
    },
    [loadData, loadJackpot, loadCurrentUser],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      refresh(true);
    }, 30 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [refresh]);

  return {
    data,
    loading,
    error,
    refresh,
    // 奖池金额相关
    jackpot,
    jackpotLoading,
    // 当前用户排名相关
    currentUser,
    currentUserLoading,
    // 今日详情相关
    todayDetail,
    todayDetailLoading,
    // 昨日奖励和回归信息相关 - 统一数据源
    yesterdayRewardInfo,
    yesterdayRewardLoading,
  };
}
